import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColumnTransform } from '@/types/query';

type BinTransform = Extract<ColumnTransform, { kind: 'bin' }>;

type Props = {
  column: string;
  value?: BinTransform;
  onChange: (next: BinTransform | null) => void;
};

type Row = { min: string; max: string; label: string };

/**
 * Ordered list of numeric bins. Each row has `min` (inclusive), `max`
 * (exclusive), and a label. Empty min/max are treated as open-ended
 * (−∞ / +∞). A trailing empty row is always shown so the user can add.
 */
export function BinEditor({ column, value, onChange }: Props): JSX.Element {
  const { t } = useTranslation();
  const [rows, setRows] = useState<Row[]>(() => toRows(value));
  const [otherLabel, setOtherLabel] = useState<string>(
    value?.otherLabel ?? '',
  );

  useEffect(() => {
    setRows(toRows(value));
    setOtherLabel(value?.otherLabel ?? '');
  }, [column, value]);

  function updateRow(i: number, patch: Partial<Row>): void {
    setRows((prev) => {
      const next = prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
      const last = next[next.length - 1];
      if (!last || last.min || last.max || last.label) {
        next.push({ min: '', max: '', label: '' });
      }
      emit(next, otherLabel);
      return next;
    });
  }

  function removeRow(i: number): void {
    setRows((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      if (next.length === 0) next.push({ min: '', max: '', label: '' });
      emit(next, otherLabel);
      return next;
    });
  }

  function setOther(next: string): void {
    setOtherLabel(next);
    emit(rows, next);
  }

  function emit(currentRows: Row[], currentOther: string): void {
    const bins: BinTransform['bins'] = [];
    for (const r of currentRows) {
      const label = r.label.trim();
      if (!label) continue;
      const min = r.min.trim() === '' ? undefined : Number(r.min);
      const max = r.max.trim() === '' ? undefined : Number(r.max);
      // Skip rows with an unparseable number — better than silently binning.
      if (min !== undefined && !Number.isFinite(min)) continue;
      if (max !== undefined && !Number.isFinite(max)) continue;
      const bin: BinTransform['bins'][number] = { label };
      if (min !== undefined) bin.min = min;
      if (max !== undefined) bin.max = max;
      bins.push(bin);
    }
    if (bins.length === 0) {
      onChange(null);
      return;
    }
    const next: BinTransform = {
      kind: 'bin',
      column,
      bins,
      ...(currentOther.trim() ? { otherLabel: currentOther.trim() } : {}),
    };
    onChange(next);
  }

  return (
    <div className="flex flex-col gap-2 text-xs">
      <div
        className="grid grid-cols-[auto_1fr_auto_1fr_2fr_auto] gap-x-2 gap-y-1 items-center"
      >
        <div style={{ color: 'var(--color-muted)' }}>
          {t('valueGroupings.bin.rangeStart')}
        </div>
        <div />
        <div style={{ color: 'var(--color-muted)' }}>
          {t('valueGroupings.bin.rangeEnd')}
        </div>
        <div />
        <div style={{ color: 'var(--color-muted)' }}>
          {t('valueGroupings.bin.label')}
        </div>
        <div />
        {rows.map((r, i) => (
          <BinRow
            key={i}
            row={r}
            onChange={(patch) => updateRow(i, patch)}
            onRemove={() => removeRow(i)}
            removable={rows.length > 1 || !!r.min || !!r.max || !!r.label}
          />
        ))}
      </div>
      <div
        className="text-[10px]"
        style={{ color: 'var(--color-muted)' }}
      >
        {t('valueGroupings.bin.boundsNote')}
      </div>

      <label className="flex items-center gap-2 mt-2">
        <span style={{ color: 'var(--color-muted)' }}>
          {t('valueGroupings.bin.fallbackLabel')}
        </span>
        <input
          type="text"
          value={otherLabel}
          onChange={(e) => setOther(e.target.value)}
          placeholder={t('valueGroupings.bin.fallbackPlaceholder')}
          className="flex-1 p-1 rounded"
          style={{ border: '1px solid var(--color-border)' }}
        />
      </label>
    </div>
  );
}

function toRows(v?: BinTransform): Row[] {
  const rows: Row[] = v
    ? v.bins.map((b) => ({
        min: b.min !== undefined ? String(b.min) : '',
        max: b.max !== undefined ? String(b.max) : '',
        label: b.label,
      }))
    : [];
  rows.push({ min: '', max: '', label: '' });
  return rows;
}

function BinRow({
  row,
  onChange,
  onRemove,
  removable,
}: {
  row: Row;
  onChange: (patch: Partial<Row>) => void;
  onRemove: () => void;
  removable: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <input
        type="number"
        value={row.min}
        onChange={(e) => onChange({ min: e.target.value })}
        className="p-1 rounded w-[80px]"
        style={{ border: '1px solid var(--color-border)' }}
      />
      <span style={{ color: 'var(--color-muted)' }}>≤ x &lt;</span>
      <input
        type="number"
        value={row.max}
        onChange={(e) => onChange({ max: e.target.value })}
        className="p-1 rounded w-[80px]"
        style={{ border: '1px solid var(--color-border)' }}
      />
      <span style={{ color: 'var(--color-muted)' }}>→</span>
      <input
        type="text"
        value={row.label}
        onChange={(e) => onChange({ label: e.target.value })}
        className="p-1 rounded"
        style={{ border: '1px solid var(--color-border)' }}
      />
      <button
        type="button"
        onClick={onRemove}
        disabled={!removable}
        aria-label={t('valueGroupings.bin.removeAria')}
        className="px-1 rounded hover:opacity-80 disabled:opacity-30"
        style={{ color: 'var(--color-muted)' }}
      >
        ×
      </button>
    </>
  );
}
